import { useEffect, useRef, useState } from 'react';
import { CalendarRange, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function CustomMonthPicker({
  label,
  name,
  value,
  error,
  onChange,
  placeholder = 'Select month',
  disabled = false,
  preferredPlacement = 'auto',
  leadingIcon: LeadingIcon,
  leadingIconClassName,
  triggerShapeClassName = 'rounded-full',
  wrapperClassName = 'relative grid w-full min-w-0 self-start gap-1.5',
  labelClassName = 'text-[0.74rem] font-medium text-[var(--theme-text-subtle)]',
  errorClassName = 'text-[0.74rem] text-[#bb5745]',
  triggerClassName = '',
  popupAlign = 'left',
}) {
  const selectedMonth = parseMonthValue(value);
  const currentYear = new Date().getFullYear();
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState('bottom');
  const [viewYear, setViewYear] = useState(selectedMonth?.year || currentYear);
  const pickerRef = useRef(null);
  const triggerRef = useRef(null);
  const selectedYear = selectedMonth?.year;

  useEffect(() => {
    if (selectedYear) {
      setViewYear(selectedYear);
    }
  }, [selectedYear]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!pickerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    function updatePlacement() {
      const triggerRect = triggerRef.current?.getBoundingClientRect();

      if (!triggerRect) {
        return;
      }

      const popupHeight = 286;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      const spaceAbove = triggerRect.top;

      setPlacement(
        preferredPlacement === 'top' || preferredPlacement === 'bottom'
          ? preferredPlacement
          : spaceBelow < popupHeight && spaceAbove > spaceBelow
            ? 'top'
            : 'bottom'
      );
    }

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    window.addEventListener('scroll', updatePlacement, true);

    return () => {
      window.removeEventListener('resize', updatePlacement);
      window.removeEventListener('scroll', updatePlacement, true);
    };
  }, [isOpen, preferredPlacement]);

  function handleSelect(monthIndex) {
    onChange({
      target: {
        name,
        value: `${viewYear}-${String(monthIndex + 1).padStart(2, '0')}`,
      },
    });
    setIsOpen(false);
  }

  function handleClearSelect() {
    onChange({
      target: {
        name,
        value: '',
      },
    });
    setIsOpen(false);
  }

  function handleThisMonthSelect() {
    const today = new Date();
    setViewYear(today.getFullYear());
    onChange({
      target: {
        name,
        value: `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`,
      },
    });
    setIsOpen(false);
  }

  return (
    <label className={wrapperClassName} ref={pickerRef}>
      <span className={labelClassName}>{label}</span>
      <button
        ref={triggerRef}
        className={`flex min-h-[40px] w-full min-w-0 items-center justify-between gap-2 ${triggerShapeClassName} border bg-white px-3 py-2 text-left text-[0.84rem] outline-none transition focus:ring-2 focus:ring-[var(--theme-accent-soft)] ${triggerClassName} ${
          error
            ? 'border-[#d78b7d] focus:border-[#c76552]'
            : 'border-[var(--theme-border)] focus:border-[var(--theme-accent)]'
        } ${selectedMonth ? 'text-[var(--theme-text)]' : 'text-[var(--theme-text-muted)]'}`}
        type="button"
        onClick={() => setIsOpen((currentValue) => !currentValue)}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        {LeadingIcon ? (
          <LeadingIcon
            className={leadingIconClassName || `h-4 w-4 shrink-0 ${error ? 'text-[#bb5745]' : 'text-[var(--theme-text-muted)]'}`}
            aria-hidden="true"
          />
        ) : null}
        <span className="min-w-0 flex-1 truncate">
          {selectedMonth ? formatMonthLabel(selectedMonth.year, selectedMonth.month) : placeholder}
        </span>
        <span className="grid h-5 w-5 shrink-0 place-items-center rounded text-[var(--theme-accent)] transition hover:bg-[var(--theme-accent-soft)]">
          <CalendarRange className="h-4 w-4" aria-hidden="true" />
        </span>
      </button>
      {error ? <span className={errorClassName}>{error}</span> : null}

      {isOpen ? (
        <div
          className={`absolute z-30 w-[min(296px,calc(100vw-2rem))] rounded-[22px] border border-[var(--theme-border)] bg-white p-3 shadow-[0_18px_45px_rgba(var(--theme-shadow-rgb),0.16)] ${
            popupAlign === 'right' ? 'right-0' : 'left-0'
          } ${
            placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'
          }`}
        >
          <div className="relative flex min-h-[38px] items-center justify-center px-11">
            <button
              className="absolute left-0 grid h-9 w-9 place-items-center rounded-xl border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={() => setViewYear((year) => year - 1)}
              aria-label="Previous year"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="text-[0.96rem] font-semibold text-[var(--theme-text-strong)]">
              {viewYear}
            </span>
            <button
              className="absolute right-0 grid h-9 w-9 place-items-center rounded-xl border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              onClick={() => setViewYear((year) => year + 1)}
              aria-label="Next year"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {MONTHS.map((monthLabel, monthIndex) => {
              const isSelected =
                selectedMonth?.year === viewYear && selectedMonth?.month === monthIndex;
              const today = new Date();
              const isCurrentMonth =
                today.getFullYear() === viewYear && today.getMonth() === monthIndex;

              return (
                <button
                  key={monthLabel}
                  className={`grid min-h-[38px] place-items-center rounded-xl text-[0.86rem] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] ${
                    isSelected
                      ? 'bg-[var(--theme-accent)] font-semibold text-white hover:bg-[var(--theme-accent-strong)]'
                      : isCurrentMonth
                        ? 'font-semibold text-[var(--theme-accent)] ring-1 ring-inset ring-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)]'
                        : 'text-[var(--theme-text-strong)] hover:bg-[var(--theme-accent-soft)]'
                  }`}
                  type="button"
                  onClick={() => handleSelect(monthIndex)}
                >
                  {monthLabel}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex items-center justify-between border-t border-[var(--theme-border)] pt-2">
            <button
              className="rounded-full border border-[var(--theme-border)] bg-white px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text-strong)]"
              type="button"
              onClick={handleClearSelect}
            >
              Clear
            </button>
            <button
              className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-warm)] px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-text-strong)]"
              type="button"
              onClick={handleThisMonthSelect}
            >
              This Month
            </button>
          </div>
        </div>
      ) : null}
    </label>
  );
}

function parseMonthValue(value) {
  const [year, month] = String(value || '').split('-').map(Number);

  if (!year || !month || month < 1 || month > 12) {
    return null;
  }

  return { year, month: month - 1 };
}

function formatMonthLabel(year, month) {
  return new Date(year, month, 1).toLocaleDateString(undefined, {
    month: 'short',
    year: 'numeric',
  });
}

export default CustomMonthPicker;
